import { useState, useEffect } from 'react';
import type { Midia } from '../types';
import { getNowSnapshot, verificarMidiaValida } from '../lib/schedule';

const TURN_CHECK_INTERVAL_MS = 60_000;

/**
 * Filtra e retorna apenas as mídias válidas para o horário atual.
 *
 * Reavaliação automática em dois momentos:
 *  1. Imediatamente quando `midias` muda (INSERT/UPDATE/DELETE via Supabase Realtime)
 *  2. A cada 60 segundos (sensor de mudança de turno — detecta virada de horário)
 */
export function useScheduledMidias(midias: Midia[]): Midia[] {
  const [active, setActive] = useState<Midia[]>(() =>
    midias.filter(m => verificarMidiaValida(m)),
  );

  useEffect(() => {
    const applyFilter = () => {
      const now = getNowSnapshot();
      const next = midias.filter(m => verificarMidiaValida(m, now));
      // Retorna a referência anterior se os objetos são os mesmos, evitando que
      // o useEffect do slideshow no Player resete o timer a cada tick de 60s.
      // Compara por referência (não por id): um UPDATE via Realtime substitui o
      // objeto em `midias`, e precisa chegar aqui mesmo com o mesmo conjunto de IDs.
      setActive(prev =>
        prev.length === next.length && prev.every((m, i) => m === next[i])
          ? prev
          : next,
      );
    };

    applyFilter();
    const interval = setInterval(applyFilter, TURN_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [midias]);

  return active;
}
